import { UNIT_TYPES } from '../services/unitsApi';

export const UNIT_TYPE_LABELS = UNIT_TYPES.reduce((acc, item) => {
  acc[item.value] = item.label;
  return acc;
}, {});

export const normalizeLabel = value => {
  if (!value) return '';
  let normalized = String(value);
  if (typeof normalized.normalize === 'function') {
    try {
      normalized = normalized.normalize('NFD');
    } catch (err) {
      // ignore environments where normalize is unsupported
    }
  }
  return normalized.replace(/[\u0300-\u036f]/g, '').replace(/\s+/g, ' ').trim().toLowerCase();
};

export const getUnitDescriptorParts = unit => {
  if (!unit) return [];
  return [
    unit?.economicNumber,
    unit?.model,
    unit?.capacity,
    UNIT_TYPE_LABELS[unit?.tractorType] || unit?.tractorType,
  ].filter(Boolean);
};

const registerLabel = (byLabel, label, unit) => {
  const normalized = normalizeLabel(label);
  if (normalized && !byLabel.has(normalized)) {
    byLabel.set(normalized, unit);
  }
};

export const buildUnitLookups = (units = []) => {
  const byId = new Map();
  const byLabel = new Map();

  units.forEach(unit => {
    if (!unit) return;

    byId.set(unit.id, unit);

    registerLabel(byLabel, unit.id, unit);
    registerLabel(byLabel, unit.model, unit);
    registerLabel(byLabel, unit.economicNumber, unit);
    registerLabel(byLabel, unit.capacity, unit);
    registerLabel(byLabel, UNIT_TYPE_LABELS[unit.tractorType] || unit.tractorType, unit);

    const descriptorParts = getUnitDescriptorParts(unit);
    if (descriptorParts.length) {
      const descriptorVariants = [
        descriptorParts.join(' - '),
        descriptorParts.join(' / '),
        descriptorParts.join(' | '),
        `Unidad - ${descriptorParts.join(' - ')}`,
      ];
      descriptorVariants.forEach(variant => registerLabel(byLabel, variant, unit));
    }
  });

  return { byId, byLabel };
};

const resolveUnitCandidate = (candidate, lookups) => {
  if (!candidate || !lookups) return null;

  if (typeof candidate === 'string' || typeof candidate === 'number') {
    return lookups.byId.get(String(candidate)) || null;
  }

  if (typeof candidate === 'object') {
    const candidateId =
      candidate?.id ??
      candidate?.value ??
      candidate?.unitId ??
      candidate?.unit ??
      candidate?.reference ??
      '';
    if (candidateId) {
      return lookups.byId.get(String(candidateId)) || null;
    }
  }

  return null;
};

export const findUnitForPublication = (publication, lookups) => {
  if (!publication || !lookups) return null;

  const directReferences = [
    publication.unitId,
    publication.unit,
    publication.unitRef,
    publication.unitReference,
  ];

  for (const reference of directReferences) {
    const resolved = resolveUnitCandidate(reference, lookups);
    if (resolved) {
      return resolved;
    }
  }

  const normalizedUnitName = normalizeLabel(publication.unitName);
  if (normalizedUnitName) {
    const byUnitName = lookups.byLabel.get(normalizedUnitName);
    if (byUnitName) return byUnitName;

    for (const [label, unit] of lookups.byLabel.entries()) {
      if (normalizedUnitName.includes(label) || label.includes(normalizedUnitName)) {
        return unit;
      }
    }
  }

  const normalizedTitle = normalizeLabel(publication.title);
  if (normalizedTitle) {
    const byTitle = lookups.byLabel.get(normalizedTitle);
    if (byTitle) return byTitle;

    for (const [label, unit] of lookups.byLabel.entries()) {
      if (normalizedTitle.includes(label) || label.includes(normalizedTitle)) {
        return unit;
      }
    }
  }

  return null;
};
